import Image from "next/image";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button, Modal } from "react-bootstrap";
import styles from "./ProductPopup.module.css";
import { useLoadScript, GoogleMap, Marker } from "@react-google-maps/api";
import { useUTMTracking } from "../../utils/useUTMTracking";

// Static libraries array to prevent reloading - exactly like working where-to-buy page
const libraries = ["places"];

export default function ProductPopup({ show, handleClose, partNo, campaignId, data }) {
  // Initialize UTM tracking
  const utmTracking = useUTMTracking({
    autoCapture: true, // Auto-capture UTM parameters
    maxAge: 30 * 60 * 1000, // 30 minutes
    persistAcrossRoutes: true
  });

  const [activeTab, setActiveTab] = useState("online");
  const [onlineStores, setOnlineStores] = useState([]); // Stores for BUY ONLINE tab
  const [nearbyStores, setNearbyStores] = useState([]); // Stores for FIND NEARBY tab
  const [allStores, setAllStores] = useState([]); // Store all available stores like where-to-buy
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [mikmakId, setMikmakId] = useState(null);
  const [partsMapData, setPartsMapData] = useState(null);
  const [partsMappingLoading, setPartsMappingLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: -25.2744, lng: 133.7751 }); // Default to Australia center
  const [storesLoading, setStoresLoading] = useState(false); // Loading state for stores data
  const [hasSearched, setHasSearched] = useState(false); // Track if user has searched for a location
   
  // Location search states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null); // Store the clicked marker/store
  const [showMarkerPopup, setShowMarkerPopup] = useState(false); // Control marker popup visibility
     
  const searchInputRef = useRef(null);
  const autoCompleteRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const mapRef = useRef(null); // Google Map instance
  const mapContainerRef = useRef(null); // DOM container (separate from mapRef)
    
  // Google Maps script loading - using the exact same approach as working where-to-buy page
  var key = process.env.NEXT_PUBLIC_GOOGLE_MAP_API;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: key,
    libraries,
  });

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // UTM-based content control function
  const getUTMControlledContent = useCallback(() => {
    const utmData = utmTracking.utmParams;
    
    // Default content
    let content = {
      showOnlineTab: true,
      showNearbyTab: true,
      defaultTab: "online",
      customMessage: null,
      storeFilter: null,
      specialOffers: false
    };

    // Control content based on UTM parameters
    if (utmData.utm_source === 'facebook' || utmData.utm_source === 'instagram') {
      // Social media traffic - emphasize online shopping
      content.defaultTab = "online";
      content.customMessage = "Special social media offer! Shop online for exclusive deals.";
      content.specialOffers = true;
    } else if (utmData.utm_source === 'google' && utmData.utm_medium === 'cpc') {
      // Google Ads traffic - show both tabs but emphasize nearby
      content.defaultTab = "nearby";
      content.customMessage = "Find stores near you or shop online!";
    } else if (utmData.utm_campaign === 'local_promotion') {
      // Local promotion campaign - emphasize nearby stores
      content.defaultTab = "nearby";
      content.customMessage = "Visit our local stores for in-person assistance!";
    } else if (utmData.utm_campaign === 'online_exclusive') {
      // Online exclusive campaign - hide nearby tab
      content.showNearbyTab = false;
      content.defaultTab = "online";
      content.customMessage = "Online exclusive offers available!";
    } else if (utmData.utm_source === 'autob') {
      // Autobarn-specific traffic - show only Autobarn stores
      content.defaultTab = "online";
      content.customMessage = "Exclusive Autobarn offer!";
      content.storeFilter = 'autobarn';
      content.specialOffers = true;
    } else if (utmData.utm_source === 'repco') {
      // Repco-specific traffic - show only Repco stores
      content.defaultTab = "online";
      content.customMessage = "Exclusive Repco offer!";
      content.storeFilter = 'repco';
      content.specialOffers = true;
    } else if (utmData.utm_source === 'autoone') {
      // Auto One-specific traffic - show only Auto One stores
      content.defaultTab = "online";
      content.customMessage = "Exclusive Auto One offer!";
      content.storeFilter = 'autoone';
      content.specialOffers = true;
    }

  

    return content;
  }, [utmTracking.utmParams]);

  // Filter online stores based on UTM parameters
  const getFilteredOnlineStores = useCallback(() => {
    const utmContent = getUTMControlledContent();

    console.log('utmContent', utmContent);
    
    if (!utmContent.storeFilter || !onlineStores || onlineStores.length === 0) {
      return onlineStores || [];
    }

    // Log retailer objects for debugging
    console.log('=== RETAILER OBJECTS DEBUG ===');
    onlineStores.forEach((store, index) => {
      console.log(`Store ${index + 1}:`, {
        storeId: store.id,
        storeName: store.name,
        retailer: store.retailer,
        retailerName: store.retailer?.name,
        retailerId: store.retailer?.id,
        retailerLogoUrl: store.retailer?.logo?.url,
        fullStoreObject: store
      });
    });
    console.log('=== END RETAILER DEBUG ===');

    // Filter stores based on UTM store filter
    const filteredStores = onlineStores.filter(store => {
      const storeName = store.retailer?.name?.toLowerCase() || '';
      const storeUrl = store.retailer?.logo?.url?.toLowerCase() || '';
      
      console.log('Filtering store:', {
        storeName,
        storeUrl,
        retailerId: store.retailer?.id,
        retailerIdType: typeof store.retailer?.id,
        filter: utmContent.storeFilter
      });
      
      if (utmContent.storeFilter === 'autobarn') {
        // Check if store is Autobarn (case-insensitive)
        const isAutobarn = storeName.includes('autobarn') || 
               storeUrl.includes('autobarn') ||
               (store.retailer?.id && typeof store.retailer.id === 'string' && store.retailer.id.toLowerCase().includes('autobarn'));
        
        console.log('Autobarn check result:', isAutobarn);
        return isAutobarn;
      } else if (utmContent.storeFilter === 'repco') {
        // Check if store is Repco (case-insensitive)
        const isRepco = storeName.includes('repco') || 
               storeUrl.includes('repco') ||
               (store.retailer?.id && typeof store.retailer.id === 'string' && store.retailer.id.toLowerCase().includes('repco'));
        
        console.log('Repco check result:', isRepco);
        return isRepco;
      } else if (utmContent.storeFilter === 'autoone') {
        // Check if store is Auto One (case-insensitive)
        const isAutoOne = storeName.includes('auto one') || 
               storeName.includes('autoone') ||
               storeUrl.includes('auto one') ||
               storeUrl.includes('autoone') ||
               (store.retailer?.id && typeof store.retailer.id === 'string' && store.retailer.id.toLowerCase().includes('auto one')) ||
               (store.retailer?.id && typeof store.retailer.id === 'string' && store.retailer.id.toLowerCase().includes('autoone'));
        
        console.log('Auto One check result:', isAutoOne);
        return isAutoOne;
      }
      
      return true; // Show all stores if no specific filter
    });

    console.log('UTM store filtering:', {
      originalCount: onlineStores.length,
      filteredCount: filteredStores.length,
      filter: utmContent.storeFilter,
      filteredStores: filteredStores.map(s => s.retailer?.name)
    });

    return filteredStores;
  }, [onlineStores, getUTMControlledContent]);

  // Function definitions - must come before useEffect hooks that use them
  
  const fetchPartsMapping = async () => {
    if (!partNo) return;
    
    setPartsMappingLoading(true);
    setError(null);
    
    try {
      console.log('Fetching parts mapping for partNo:', partNo);
      const response = await fetch(`/api/parts-mapping-simple?stockCode=${partNo}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Parts mapping response:', data);
      
      if (data.success && data.partsMap) {
        const makmakId = data.partsMap.makmakId;
        setMikmakId(makmakId);
        setPartsMapData(data.partsMap);
        console.log('Set mikmakId to:', makmakId);
      } else {
        console.log('No parts mapping found, response:', data);
        setError(`No parts mapping found for this product: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error fetching parts mapping:', err);
      setError(`Failed to load parts mapping data: ${err.message}`);
    } finally {
      setPartsMappingLoading(false);
    }
  };

  const fetchStoreAvailability = async () => {
    if (!mikmakId) {
      console.log('No mikmakId available, skipping store fetch');
      return;
    }
    
    console.log('Fetching store availability for mikmakId:', mikmakId);
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/mikmak-availability?mikmakId=${mikmakId}&country=AU&zipCode=3000`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Store availability response:', data);
      
      if (data.success && data.stores) {
        setOnlineStores(data.stores);
        console.log('Set online stores:', data.stores.length);
      } else {
        console.log('No stores in response:', data);
        setError('No store data available');
      }
    } catch (err) {
      console.error('Error fetching store availability:', err);
      setError('Failed to load store data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all stores like the where-to-buy page
  const fetchAllStores = useCallback(async () => {
    try {
      setStoresLoading(true);
      console.log('Fetching all stores like where-to-buy page...');
      console.log('API URL: /api/whereToBuyStore');
      
      const response = await fetch('/api/whereToBuyStore');
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('All stores response:', data);
      console.log('Response keys:', Object.keys(data));
      console.log('allStores exists:', !!data.allStores);
      console.log('allStores length:', data.allStores?.length);
      
      if (data.allStores && data.allStores.length > 0) {
        setAllStores(data.allStores);
        console.log('Set all stores:', data.allStores.length);
        console.log('allStores state updated successfully');
      } else {
        console.log('No stores found in response');
        setAllStores([]);
      }
    } catch (err) {
      console.error('Error fetching all stores:', err);
      setAllStores([]);
    } finally {
      setStoresLoading(false);
    }
  }, []);

  // Map load handler
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Update map center when stores are found
  const updateMapCenter = useCallback((stores) => {
    if (stores && stores.length > 0) {
      const validStores = stores.filter(store => 
        store.flatData?.location?.latitude && store.flatData?.location?.longitude
      );
      
      if (validStores.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        validStores.forEach(store => {
          bounds.extend({
            lat: parseFloat(store.flatData.location.latitude),
            lng: parseFloat(store.flatData.location.longitude)
          });
        });
        
        if (mapRef.current && typeof mapRef.current.fitBounds === 'function') {
          mapRef.current.fitBounds(bounds);
        }
      }
    }
  }, []);

  // Update map function similar to ResultView page - filter stores based on location
  const updateMap = useCallback((selectedAddress) => {
    console.log('updateMap function called with:', selectedAddress);
    
    if (!allStores || allStores.length === 0) {
      console.log('No allStores available for filtering, allStores:', allStores);
      return;
    }
    
    const totalAUStore = allStores.length;
    const searchList = [];
    
    const start = {
      latitude: selectedAddress?.lat,
      longitude: selectedAddress?.lon,
    };
    
    console.log('Starting point:', start);
    
    // Filter stores within radius
    for (let i = 0; i < totalAUStore; i++) {
      const store = allStores[i];
      const end = {
        latitude: store?.flatData?.location?.latitude,
        longitude: store?.flatData?.location?.longitude,
      };
      
      if (start && end && start.latitude && start.longitude && end.latitude && end.longitude) {
        // Calculate distance between points
        const distance = calculateDistance(start, end);
        
        // Filter stores within 3km radius
        if (distance <= 3) {
          store.distance = distance;
          searchList.push(store);
        }
      }
    }
    
    // Sort by distance
    searchList.sort((a, b) => a.distance - b.distance);
    
    console.log('Filtered stores:', searchList.length);
    setNearbyStores(searchList);
    setError(null);
    
    // Update map center and fit bounds to show all stores
    if (searchList.length > 0) {
      updateMapCenter(searchList);
    }
  }, [allStores, updateMapCenter]);

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (point1, point2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (point2.latitude - point1.latitude) * Math.PI / 180;
    const dLon = (point2.longitude - point1.longitude) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(point1.latitude * Math.PI / 180) * Math.cos(point2.latitude * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in kilometers
  };

  // Handle marker click to show store details popup
  const handleMarkerClick = (store, index) => {
    setSelectedMarker({ ...store, index });
    setShowMarkerPopup(true);
  };

  // Focus/pan the map to a specific store and open its popup
  const focusStoreOnMap = (store, index) => {
    try {
      const lat = parseFloat(store?.flatData?.location?.latitude);
      const lng = parseFloat(store?.flatData?.location?.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        // Ensure we are on the Nearby tab
        if (activeTab !== 'nearby') {
          setActiveTab('nearby');
        }
        if (mapRef.current && typeof mapRef.current.panTo === 'function') {
          mapRef.current.panTo({ lat, lng });
          if (typeof mapRef.current.setZoom === 'function') {
            mapRef.current.setZoom(14);
          }
        } else {
          // Fallback – update center so the map re-renders
          setMapCenter({ lat, lng });
        }
        // Open the marker popup for this store
        handleMarkerClick(store, index);
      }
    } catch (e) {
      console.error('Failed to focus map for store', store, e);
    }
  };

  // Close marker popup
  const closeMarkerPopup = () => {
    setShowMarkerPopup(false);
    setSelectedMarker(null);
  };

    const handleLocationSelect = (place) => {
    console.log('Location selected:', place);
    setSelectedLocation(place);
    
    // Build the full address from address_components if formatted_address is not available
    let fullAddress = place.formatted_address;
    let postcode = '3000'; // Default postcode
    
    if (!fullAddress && place.address_components && place.address_components.length > 0) {
      const parts = [];
      
      const streetNumber = place.address_components.find(comp => comp.types.includes('street_number'));
      const route = place.address_components.find(comp => comp.types.includes('route'));
      const locality = place.address_components.find(comp => comp.types.includes('locality'));
      const state = place.address_components.find(comp => comp.types.includes('administrative_area_level_1'));
      const postalCode = place.address_components.find(comp => comp.types.includes('postal_code'));
      const country = place.address_components.find(comp => comp.types.includes('country'));
      
      if (streetNumber && route) parts.push(`${streetNumber.long_name} ${route.long_name}`);
      if (locality) parts.push(locality.long_name);
      if (state) parts.push(state.short_name);
      if (postalCode) postcode = postalCode.long_name;
      if (country) parts.push(country.long_name);
      
      fullAddress = parts.join(', ');
    }
    
    if (!fullAddress) {
      fullAddress = place.description || place.name || '';
    }
    
    setSearchQuery(fullAddress);
    console.log('Setting search query to:', fullAddress);
    
    setNearbyStores([]);
    setError(null);
    setHasSearched(false);
    
    if (place.geometry?.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setMapCenter({ lat, lng });
    }
    
    // Automatically trigger search after location selection
    console.log('Starting automatic search with allStores:', allStores?.length);
    
    if (allStores && allStores.length > 0) {
      const locationData = {
        lat: place.geometry?.location?.lat(),
        lon: place.geometry?.location?.lng(),
        postcode: postcode || '3000',
        state: place.address_components?.find(comp => 
          comp.types.includes('administrative_area_level_1')
        )?.short_name || '',
        country: place.address_components?.find(comp => 
          comp.types.includes('country')
        )?.short_name || '',
        storeList: [],
        distance: 3
      };
      
      console.log('Auto-triggering search for location:', locationData);
      updateMap(locationData);
      setHasSearched(true);
    } else {
      console.log('allStores not available, falling back to API call');
      if (mikmakId) {
        console.log('Using mikmakId fallback:', mikmakId);
        fetchStoreAvailabilityForLocation(place);
        setHasSearched(true);
      } else {
        console.log('No mikmakId available for fallback');
      }
    }
  };

  const fetchStoreAvailabilityForLocation = async (location) => {
    if (!mikmakId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching stores for location:', location);
      
      let postcode = '3000';
      
      if (location.formatted_address) {
        const postcodeMatch = location.formatted_address.match(/\b\d{4}\b/);
        postcode = postcodeMatch ? postcodeMatch[0] : postcode;
      }
      
      console.log('Extracted postcode:', postcode);
      
      const response = await fetch(`/api/mikmak-availability?mikmakId=${mikmakId}&country=AU&zipCode=${postcode}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.stores) {
        setNearbyStores(data.stores);
        console.log('Nearby stores updated for location:', postcode);
      } else {
        setError('No store data available for this location');
      }
    } catch (err) {
      console.error('Error fetching store availability for location:', err);
      setError('Failed to load store data for this location');
    } finally {
      setLoading(false);
    }
  };

  // useEffect hooks - must come after function definitions
  
  // Fetch parts mapping data when modal opens
  useEffect(() => {
    if (show && partNo && mounted) {
      fetchPartsMapping();
    }
  }, [show, partNo, mounted]);

  // Apply UTM-based content control when modal opens
  useEffect(() => {
    if (show && mounted) {
      console.log('ProductPopup UTM Debug:', {
        isTracking: utmTracking.isTracking,
        utmParams: utmTracking.utmParams,
        source: utmTracking.source,
        medium: utmTracking.medium,
        campaign: utmTracking.campaign
      });
      
      const utmContent = getUTMControlledContent();
      console.log('UTM-controlled content:', utmContent);
      
      // Log UTM data for debugging
      if (utmTracking.isTracking) {
        console.log('UTM tracking active:', utmTracking.utmParams);
      } else {
        console.log('No UTM tracking data available');
      }
    }
  }, [show, mounted, getUTMControlledContent, utmTracking.isTracking, utmTracking.utmParams]);

  // Fetch store availability data after getting mikmakId
  useEffect(() => {
    if (mikmakId && mounted) {
      fetchStoreAvailability();
    }
  }, [mikmakId, mounted]);

  // Fetch all stores when modal opens (but don't display them yet)
  useEffect(() => {
    if (show && mounted) {
      console.log('Modal opened, fetching all stores...');
      fetchAllStores();
      
      // Retry if stores are not loaded after a delay
      const retryTimer = setTimeout(() => {
        if (allStores.length === 0) {
          console.log('Retrying fetchAllStores after delay...');
          fetchAllStores();
        }
      }, 1000);
      
      setHasSearched(false);
      setNearbyStores([]);
      setError(null);
      
      return () => clearTimeout(retryTimer);
    }
  }, [show, mounted, fetchAllStores]);

  // Setup autocomplete when modal opens and Google Maps is loaded
  useEffect(() => {
    if (!show || !isLoaded) return;
    
    // Prevent multiple setups
    if (autoCompleteRef.current) {
      console.log('Autocomplete already set up, skipping...');
      return;
    }
    
    // Only setup autocomplete when on FIND NEARBY tab
    if (activeTab !== "nearby") {
      console.log('Not on FIND NEARBY tab, skipping autocomplete setup...');
      return;
    }
    
    console.log('Setting up autocomplete for FIND NEARBY tab...');

    const setupAutocomplete = () => {
      console.log('setupAutocomplete called with:', {
        hasSearchInput: !!searchInputRef.current,
        hasAutoComplete: !!autoCompleteRef.current,
        hasGoogle: !!window.google,
        hasMaps: !!(window.google && window.google.maps),
        hasPlaces: !!(window.google && window.google.maps && window.google.maps.places)
      });
      
      if (searchInputRef.current && !autoCompleteRef.current && window.google && window.google.maps && window.google.maps.places) {
        console.log('Setting up autocomplete...');
        
        try {
          const options = {
            componentRestrictions: { country: "au" },
            fields: ["address_components", "geometry", "icon", "name", "formatted_address"],
            types: [
              "locality",
              "postal_code",
              "sublocality",
              "administrative_area_level_1",
              "administrative_area_level_2",
            ],
          };

          console.log('Creating Autocomplete with options:', options);

          autoCompleteRef.current = new window.google.maps.places.Autocomplete(
            searchInputRef.current,
            options
          );

          console.log('Google Maps Autocomplete created successfully');

          // Test if autocomplete is working
          console.log('Testing autocomplete functionality...');
          console.log('Autocomplete instance:', autoCompleteRef.current);
          console.log('Search input element:', searchInputRef.current);
          console.log('Search input value:', searchInputRef.current?.value);
          console.log('Search input placeholder:', searchInputRef.current?.placeholder);

          // Add CSS for autocomplete styling
          const style = document.createElement('style');
          style.textContent = `
            .pac-container {
              z-index: 99999 !important;
              background: white !important;
              border: 1px solid #ccc !important;
              border-radius: 4px !important;
              box-shadow: 0 2px 6px rgba(0,0,0,0.3) !important;
              font-family: Arial, sans-serif !important;
              font-size: 14px !important;
              margin-top: 0 !important;
              max-height: 200px !important;
              overflow-y: auto !important;
              min-width: 200px !important;
            }
            .pac-item {
              padding: 8px 12px !important;
              cursor: pointer !important;
              border-bottom: 1px solid #eee !important;
              margin: 0 !important;
              white-space: nowrap !important;
              overflow: hidden !important;
              text-overflow: ellipsis !important;
            }
            .pac-item:last-child {
              border-bottom: none !important;
            }
            .pac-item:hover {
              background-color: #f5f5f5 !important;
            }
            .pac-item-selected {
              background-color: #e3f2fd !important;
            }
            .pac-container:after {
              content: none !important;
            }
            .pac-item-query {
              font-weight: bold !important;
            }
            .pac-matched {
              font-weight: bold !important;
              color: #000 !important;
            }
          `;
          document.head.appendChild(style);

          // Listen for place selection
          autoCompleteRef.current.addListener("place_changed", async function () {
            console.log('Place selection triggered');
            const place = await autoCompleteRef.current.getPlace();
            console.log('Selected place:', place);
            
            if (place.geometry && place.geometry.location) {
              console.log('Place has valid geometry, calling handleLocationSelect');
              const selectedPlace = {
                ...place,
                description: place.formatted_address || place.name,
                place_id: place.place_id,
                geometry: place.geometry,
                formatted_address: place.formatted_address
              };
              
              // Call handleLocationSelect immediately without timeout
              handleLocationSelect(selectedPlace);
            } else {
              console.log('No valid geometry found for place:', place);
            }
          });

          // Test autocomplete by triggering a focus event
          setTimeout(() => {
            console.log('Testing autocomplete focus...');
            if (searchInputRef.current) {
              searchInputRef.current.focus();
              console.log('Search input focused for testing');
            }
          }, 1000);

          console.log('Google Maps Autocomplete setup complete');

        } catch (error) {
          console.error('Error setting up Google Maps Autocomplete:', error);
        }
      } else {
        console.log('Cannot setup autocomplete - missing requirements:', {
          hasSearchInput: !!searchInputRef.current,
          hasAutoComplete: !!autoCompleteRef.current,
          hasGoogle: !!window.google,
          hasMaps: !!(window.google && window.google.maps),
          hasPlaces: !!(window.google && window.google.maps && window.google.maps.places)
        });
      }
    };

    // Wait for both stores AND search input to be available
    const waitForSetup = () => {
      console.log('Checking setup requirements...');
      console.log('allStores.length:', allStores.length);
      console.log('searchInputRef.current:', !!searchInputRef.current);
      console.log('activeTab:', activeTab);
      
      if (allStores.length > 0 && searchInputRef.current) {
        console.log('Both stores and search input available, setting up autocomplete...');
        setupAutocomplete();
      } else if (allStores.length > 0 && !searchInputRef.current) {
        console.log('Stores loaded but search input not ready, waiting for input...');
        setTimeout(waitForSetup, 100);
      } else if (allStores.length === 0) {
        console.log('Stores not loaded yet, waiting...');
        setTimeout(waitForSetup, 500);
      } else {
        console.log('Waiting for setup requirements...');
        setTimeout(waitForSetup, 100);
      }
    };
    
    // Start waiting for setup requirements
    waitForSetup();
    
    return () => {
      // Cleanup will be handled by the component unmounting
    };
  }, [show, isLoaded, allStores.length, activeTab]);

  // Setup autocomplete when switching to FIND NEARBY tab
  useEffect(() => {
    if (activeTab === "nearby" && isLoaded && !autoCompleteRef.current) {
      console.log('Switched to FIND NEARBY tab, setting up autocomplete...');
      
      // Small delay to ensure the search input is rendered
      const setupTimer = setTimeout(() => {
        if (searchInputRef.current && allStores.length > 0) {
          console.log('Setting up autocomplete after tab switch...');
          
          try {
            const options = {
              componentRestrictions: { country: "au" },
              fields: ["address_components", "geometry", "icon", "name", "formatted_address"],
              types: [
                "locality",
                "postal_code",
                "sublocality",
                "administrative_area_level_1",
                "administrative_area_level_2",
              ],
            };

            autoCompleteRef.current = new window.google.maps.places.Autocomplete(
              searchInputRef.current,
              options
            );

            console.log('Google Maps Autocomplete created after tab switch');

            // Add CSS for autocomplete styling (if not already added)
            if (!document.querySelector('#autocomplete-styles')) {
              const style = document.createElement('style');
              style.id = 'autocomplete-styles';
              style.textContent = `
                .pac-container {
                  z-index: 99999 !important;
                  background: white !important;
                  border: 1px solid #ccc !important;
                  border-radius: 4px !important;
                  box-shadow: 0 2px 6px rgba(0,0,0,0.3) !important;
                  font-family: Arial, sans-serif !important;
                  font-size: 14px !important;
                  margin-top: 0 !important;
                  max-height: 200px !important;
                  overflow-y: auto !important;
                  min-width: 200px !important;
                }
                .pac-item {
                  padding: 8px 12px !important;
                  cursor: pointer !important;
                  border-bottom: 1px solid #eee !important;
                  margin: 0 !important;
                  white-space: nowrap !important;
                  overflow: hidden !important;
                  text-overflow: ellipsis !important;
                }
                .pac-item:last-child {
                  border-bottom: none !important;
                }
                .pac-item:hover {
                  background-color: #f5f5f5 !important;
                }
                .pac-item-selected {
                  background-color: #e3f2fd !important;
                }
                .pac-container:after {
                  content: none !important;
                }
                .pac-item-query {
                  font-weight: bold !important;
                }
                .pac-matched {
                  font-weight: bold !important;
                  color: #000 !important;
                }
              `;
              document.head.appendChild(style);
            }

            // Listen for place selection
            autoCompleteRef.current.addListener("place_changed", async function () {
              console.log('Place selection triggered');
              const place = await autoCompleteRef.current.getPlace();
              console.log('Selected place:', place);
              
              if (place.geometry && place.geometry.location) {
                console.log('Place has valid geometry, calling handleLocationSelect');
                const selectedPlace = {
                  ...place,
                  description: place.formatted_address || place.name,
                  place_id: place.place_id,
                  geometry: place.geometry,
                  formatted_address: place.formatted_address
                };
                
                // Call handleLocationSelect immediately without timeout
                handleLocationSelect(selectedPlace);
              } else {
                console.log('No valid geometry found for place:', place);
              }
            });

            console.log('Google Maps Autocomplete setup complete after tab switch');

          } catch (error) {
            console.error('Error setting up Google Maps Autocomplete after tab switch:', error);
          }
        } else {
          console.log('Search input or stores not ready after tab switch');
        }
      }, 300); // 300ms delay to ensure DOM is ready

      return () => clearTimeout(setupTimer);
    }
  }, [activeTab, isLoaded, allStores.length]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted) {
    return null;
  }

  // Safety check for required props
  if (!partNo) {
    console.error('ProductPopup: partNo is required');
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size='md'
      keyboard={false}
      dialogClassName={styles['ryco-modal']}
    >
      <div className={`${styles['ryco-modal-content']} border border-5 border-white`}>
        {/* Header */}
        <div className={`${styles['ryco-header']} text-center text-white pb-0 position-relative`}>
          {/* Close Button */}
          <button
            onClick={handleClose}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              backgroundColor: "#fff",
              borderRadius: "50%",
              border: "none",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              fill='black'
              viewBox='0 0 16 16'
            >
              <path d='M2.146 2.146a.5.5 0 0 1 .708 0L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854a.5.5 0 0 1 0-.708z' />
            </svg>
          </button>

          <Image
            src='/img/logo.svg'
            alt='RYCO'
            width={200}
            height={60}
            className='my-4'
            style={{
              filter: 'brightness(0) invert(1)',
              maxWidth: '100%',
              height: 'auto'
            }}
            onError={(e) => {
              console.error('Failed to load RYCO logo');
              e.target.style.display = 'none';
            }}
          />
          
          <div className='fs-3 mb-5'>
            <span className='fw-bold'>{partNo}</span> {data?.webDescription || 'SYNTEC OIL FILTER'}
          </div>
          
          
          
          {/* Tabs */}
          <div className={`d-flex mt-3 ${styles['ryco-tabs']}`}>
            {/* UTM-controlled content */}
            {(() => {
              const utmContent = getUTMControlledContent();
              
              return (
                <>
                  {utmContent.showOnlineTab && (
                    <button
                      className={`flex-fill ${styles['tab-btn']} ${
                        activeTab === "online" ? styles['active'] : ""
                      }`}
                      onClick={() => {
                        setActiveTab("online");
                        setNearbyStores([]);
                        setHasSearched(false);
                        setError(null);
                      }}
                      style={{ fontSize: "20px", fontWeight: "600" }}
                    >
                      BUY ONLINE
                    </button>
                  )}
                  
                  {utmContent.showNearbyTab && (
                    <button
                      className={`flex-fill ${styles['tab-btn']} ${
                        activeTab === "nearby" ? styles['active'] : ""
                      }`}
                      onClick={() => {
                        setActiveTab("nearby");
                        setError(null);
                      }}
                      style={{ fontSize: "20px", fontWeight: "600" }}
                    >
                      FIND NEARBY
                    </button>
                  )}
                </>
              );
            })()}
          </div>
          
          {/* UTM Custom Message */}
          {(() => {
            const utmContent = getUTMControlledContent();
            return utmContent.customMessage ? (
              <div className="text-center py-2" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                <p className="mb-0 fw-bold" style={{ color: '#ED1C24', fontSize: '14px' }}>
                  {utmContent.customMessage}
                </p>
              </div>
            ) : null;
          })()}
        </div>

        {/* Store list */}
        <div className='mx-0'>
          {activeTab === "online" && (
            <div
              className='px-3'
              style={{
                maxHeight: "450px",
                overflowY: "auto",
              }}
            >
              {partsMappingLoading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading product data...</p>
                </div>
              )}
               
              {loading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading store availability...</p>
                </div>
              )}
               
              {error && (
                <div className="text-center py-4 text-danger">
                  <p>{error}</p>
                </div>
              )}
               
                             {!partsMappingLoading && !loading && !error && (!getFilteredOnlineStores() || getFilteredOnlineStores().length === 0) && (
                <div className="text-center py-4 text-muted">
                  <p>No stores available for this product</p>
                </div>
              )}
               
                             {!loading && !error && getFilteredOnlineStores() && getFilteredOnlineStores().length > 0 && getFilteredOnlineStores().map((store, index) => {
                try {
                  return (
                    <div
                      key={store.id || index}
                      className='d-flex align-items-center justify-content-between py-5'
                      style={{
                        cursor: "pointer",
                        borderBottom: "1px solid #ED1C24",
                      }}
                    >
                      {/* Store Logo */}
                      <div className='d-flex align-items-center'>
                        {store.retailer?.logo?.url && store.retailer.logo.url.startsWith('http') ? (
                          <img
                            src={store.retailer.logo.url}
                            alt={store.retailer.name || 'Store logo'}
                            width={120}
                            height={50}
                            style={{ objectFit: 'contain' }}
                            onError={(e) => {
                              console.error('Failed to load store logo:', store.retailer.logo.url);
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : null}
                      </div>

                      {/* Stock Status & Price */}
                      <div className='d-flex flex-column align-items-center'>
                        <div
                          className='d-flex align-items-center fw-bold mb-1'
                          style={{ color: store.stockStatus === "Available" ? "#70bd40" : "#dc3545" }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='1.2rem'
                            height='1.2rem'
                            fill='currentColor'
                            className='me-1'
                            viewBox='0 0 16 16'
                          >
                            <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.07 0l3.992-3.992a.75.75 0 1 0-1.06-1.06L7.5 9.44 5.53 7.47a.75.75 0 0 0-1.06 1.06l2.5 2.5z' />
                          </svg>
                          <span className='d-none d-md-inline'>
                            {store.stockStatus === "Available" ? "IN STOCK" : "OUT OF STOCK"}
                          </span>
                        </div>
                        
                        {/* {store.price && (
                          <div className="text-center">
                            <span className="fw-bold text-primary">
                              {store.price.currency} {store.price.value}
                            </span>
                          </div>
                        )} */}
                      </div>

                      {/* Buy Now */}
                      <Button
                        variant='danger'
                        className='fw-bold px-4 py-2'
                        style={{
                          borderRadius: 0,
                          backgroundColor: "#ED1C24",
                          border: "none",
                        }}
                        onClick={() => {
                          if (store.clickUrl) {
                            window.open(store.clickUrl, '_blank');
                          }
                        }}
                        disabled={store.stockStatus !== "Available"}
                      >
                        BUY NOW
                      </Button>
                    </div>
                  );
                } catch (error) {
                  console.error('Error rendering store item:', error, store);
                  return (
                    <div key={store.id || index} className="text-center py-3 text-danger">
                      Error rendering store: {store.name || 'Unknown store'}
                    </div>
                  );
                }
              })}
            </div>
          )}

          {activeTab === "nearby" && (
            <div>
              {/* Map container with search */}
              {loadError ? (
                <div className="text-center py-4 text-danger">
                  <p>Error loading Google Maps: {loadError.message}</p>
                  <p className="small">Please check your internet connection and try again.</p>
                </div>
              ) : !isLoaded ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading Google Maps...</span>
                  </div>
                  <p className="mt-2">Loading Google Maps...</p>
                </div>
              ) : (
                <div style={{ position: "relative", height: "400px" }}>
                  {/* Search bar overlay */}
                  <div
                    style={{
                      position: "absolute",
                      top: "20px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "#fff",
                      borderRadius: "25px",
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 20px",
                      width: "85%",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                    }}
                  >
                    <input
                      ref={searchInputRef}
                      type='text'
                      placeholder='ENTER LOCATION OR POSTCODE'
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                      }}
                      onFocus={() => {
                        console.log('Search input focused');
                      }}
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        fontSize: "16px",
                        padding: "10px 0",
                        background: "transparent",
                        color: "#333",
                        fontWeight: "500",
                      }}
                    />

                    <button
                      onClick={() => {
                        if (searchQuery && searchQuery.trim()) {
                          // Try to use the search query to find a place
                          if (window.google && window.google.maps && window.google.maps.places) {
                            const geocoder = new window.google.maps.Geocoder();
                            geocoder.geocode({ address: searchQuery, componentRestrictions: { country: 'AU' } }, (results, status) => {
                              if (status === 'OK' && results[0]) {
                                const place = {
                                  ...results[0],
                                  description: results[0].formatted_address,
                                  place_id: results[0].place_id,
                                  geometry: results[0].geometry,
                                  formatted_address: results[0].formatted_address
                                };
                                handleLocationSelect(place);
                              } else {
                                setError('Could not find location. Please try a different search term.');
                              }
                            });
                          } else {
                            setError('Google Maps not loaded. Please wait and try again.');
                          }
                        } else {
                          setError('Please enter a location or postcode first.');
                        }
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "8px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "#f5f5f5"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                      title="Search for nearby stores"
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        fill='#666'
                        viewBox='0 0 16 16'
                      >
                        <path
                          d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242.656a5 5 0 1 1 0-10 5 5 0 0 1 0 10z'
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Google Map with markers */}
                  <div
                    ref={mapContainerRef}
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative'
                    }}
                  >
                    <GoogleMap
                      mapContainerStyle={{
                        width: '100%',
                        height: '100%'
                      }}
                      center={mapCenter}
                      zoom={4}
                      options={{
                        zoomControl: true,
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                        styles: [
                          {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }]
                          }
                        ]
                      }}
                      onLoad={(map) => { mapRef.current = map; }}
                      onClick={closeMarkerPopup}
                    >
                      {/* Store markers */}
                      {nearbyStores && nearbyStores.length > 0 && nearbyStores.map((store, index) => {
                        if (store.flatData?.location?.latitude && store.flatData?.location?.longitude) {
                          return (
                            <Marker
                              key={store.id || index}
                              position={{
                                lat: parseFloat(store.flatData.location.latitude),
                                lng: parseFloat(store.flatData.location.longitude)
                              }}
                              label={{
                                text: (index + 1).toString(),
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                              icon={{
                                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                  <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="20" cy="20" r="18" fill="#ED1C24" stroke="white" stroke-width="2"/>
                                    <text x="20" y="25" text-anchor="middle" fill="white" font-size="14" font-weight="bold">${index + 1}</text>
                                  </svg>
                                `),
                                scaledSize: new window.google.maps.Size(40, 40),
                                anchor: new window.google.maps.Point(20, 20)
                              }}
                              onClick={() => handleMarkerClick(store, index)}
                            />
                          );
                        }
                        return null;
                      })}
                    </GoogleMap>
                  </div>

                   {/* Marker Popup Overlay */}
                   {showMarkerPopup && selectedMarker && (
                     <div
                       style={{
                         position: "absolute",
                         top: "50%",
                         left: "50%",
                         transform: "translate(-50%, -50%)",
                         background: "#fff",
                         borderRadius: "12px",
                         padding: "20px",
                         boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                         zIndex: 2000,
                         maxWidth: "350px",
                         width: "90%",
                         border: "2px solid #ED1C24"
                       }}
                     >
                       {/* Close Button */}
                       <button
                         onClick={closeMarkerPopup}
                         style={{
                           position: "absolute",
                           top: "10px",
                           right: "10px",
                           background: "none",
                           border: "none",
                           fontSize: "20px",
                           cursor: "pointer",
                           color: "#666",
                           width: "30px",
                           height: "30px",
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "center"
                         }}
                       >
                         ×
                       </button>

                       {/* Store Number Badge */}
                       <div className="d-flex align-items-center mb-3">
                         <div 
                           className="me-3 d-flex align-items-center justify-content-center"
                           style={{
                             width: '40px',
                             height: '40px',
                             backgroundColor: '#ED1C24',
                             borderRadius: '50%',
                             color: 'white',
                             fontWeight: 'bold',
                             fontSize: '18px'
                           }}
                         >
                           {selectedMarker.index + 1}
                         </div>
                         <h5 className="fw-bold mb-0" style={{ color: "#00293B" }}>
                           {selectedMarker.flatData?.title || selectedMarker.name || 'Store'}
                         </h5>
                       </div>

                       {/* Store Address */}
                       {selectedMarker.flatData?.address && (
                         <p className="mb-2 small text-muted">
                           <span className="me-2">📍</span>
                           {selectedMarker.flatData.address}
                         </p>
                       )}
                       
                       {selectedMarker.flatData?.suburb && (
                         <p className="mb-2 small text-muted">
                           <span className="me-2">🏢</span>
                           {selectedMarker.flatData.suburb}, {selectedMarker.flatData.state} {selectedMarker.flatData.postcode}
                         </p>
                       )}

                       {/* Phone Number */}
                       {(selectedMarker.flatData?.phone || selectedMarker.phone || selectedMarker.contact?.phone) ? (
                         <p className="mb-3 small text-muted">
                           <span className="me-2">📞</span>
                           <a href={`tel:${selectedMarker.flatData?.phone || selectedMarker.phone || selectedMarker.contact?.phone}`} className="text-decoration-none text-muted">
                             {selectedMarker.flatData?.phone || selectedMarker.phone || selectedMarker.contact?.phone}
                           </a>
                         </p>
                       ) : (
                         <p className="mb-3 small text-muted">
                           <span className="me-2">📞</span>
                           <span className="text-muted">Phone: Not available</span>
                         </p>
                       )}

                       {/* Distance */}
                       {selectedMarker.distance && (
                         <p className="mb-3 small text-muted">
                           <span className="me-2">📏</span>
                           Distance: {selectedMarker.distance.toFixed(1)} km
                         </p>
                       )}

                       {/* Get Directions Button */}
                       {selectedMarker.flatData?.location?.latitude && selectedMarker.flatData?.location?.longitude && (
                         <div className="mt-3">
                           <a
                             href={`https://www.google.com/maps/dir/?api=1&destination=${selectedMarker.flatData.location.latitude},${selectedMarker.flatData.location.longitude}`}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="btn btn-outline-danger btn-sm w-100"
                             style={{ borderRadius: "8px", fontWeight: "600" }}
                           >
                             Get Directions
                           </a>
                         </div>
                       )}
                     </div>
                   )}
                 </div>
               )}

              {/* Instruction text */}
              <div className="text-center py-4">
                <p className="fw-bold text-uppercase mb-0" style={{ color: "#00293B", fontSize: "16px" }}>
                  PLEASE ENTER YOUR LOCATION ABOVE THE MAP TO FIND STORES NEAR YOU.
                </p>
              </div>

              {/* Store Results Section */}
              {loading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading stores...</span>
                  </div>
                  <p className="mt-2">Searching for nearby stores...</p>
                </div>
              )}

              {error && (
                <div className="text-center py-3 text-danger">
                  <p className="mb-0">{error}</p>
                </div>
              )}

              {!loading && !error && hasSearched && nearbyStores && nearbyStores.length > 0 && (
                <div className="px-4">
                  <div className="row">
                    <div className="col-12">
                      <h6 className="fw-bold mb-4" style={{ color: "#00293B", fontSize: "18px" }}>
                        Nearby Stores Found ({nearbyStores.length})
                      </h6>
                    </div>
                  </div>
                  <div className="row">
                    {nearbyStores.map((store, index) => (
                      <div key={store.id || index} className="col-md-6 mb-4">
                        <div className="card p-4 border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
                          <div className="d-flex align-items-center mb-3" style={{cursor:'pointer'}} onClick={() => focusStoreOnMap(store, index)}>
                            <div 
                              className="me-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: '35px',
                                height: '35px',
                                backgroundColor: '#ED1C24',
                                borderRadius: '50%',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '16px'
                              }}
                            >
                              {index + 1}
                            </div>
                            <h6 className="fw-bold mb-0" style={{ color: "#00293B" }}>
                              {store.flatData?.title || store.name || 'Store'}
                            </h6>
                          </div>
                         
                          {store.flatData?.address && (
                            <p className="mb-2 small text-muted">
                              <span className="me-2">📍</span>
                              {store.flatData.address}
                            </p>
                          )}
                          
                          {store.flatData?.suburb && (
                            <p className="mb-2 small text-muted">
                              <span className="me-2">🏢</span>
                              {store.flatData.suburb}, {store.flatData.state} {store.flatData.postcode}
                            </p>
                          )}
                          
                          {/* Phone number */}
                          {(store.flatData?.phone || store.phone || store.contact?.phone) ? (
                            <p className="mb-3 small text-muted">
                              <span className="me-2">📞</span>
                              <a href={`tel:${store.flatData?.phone || store.phone || store.contact?.phone}`} className="text-decoration-none text-muted">
                                {store.flatData?.phone || store.phone || store.contact?.phone}
                              </a>
                            </p>
                          ) : (
                            <p className="mb-3 small text-muted">
                              <span className="me-2">📞</span>
                              <span className="text-muted">Phone: Not available</span>
                            </p>
                          )}
                         
                          {store.distance && (
                            <p className="mb-3 small text-muted">
                              <span className="me-2">📏</span>
                              Distance: {store.distance.toFixed(1)} km
                            </p>
                          )}
                          
                          {store.flatData?.location?.latitude && store.flatData?.location?.longitude && (
                            <div className="mt-auto">
                              <a
                                href={`https://www.google.com/maps/dir/?api=1&destination=${store.flatData.location.latitude},${store.flatData.location.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-danger btn-sm w-100"
                                style={{ borderRadius: "8px", fontWeight: "600" }}
                              >
                                Get Directions
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!loading && !error && hasSearched && nearbyStores && nearbyStores.length === 0 && searchQuery && (
                <div className="text-center py-4 text-muted">
                  <p className="mb-0">No stores found within 3km of this location. Try a different area.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
} 